import { useState } from 'react';
import { useAuth } from './useAuth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

export interface MigrationResult {
  success: boolean;
  progress?: {
    totalConversations: number;
    processedConversations: number;
    enhancedConversations: number;
    errors: number;
    startTime: string;
  };
  message?: string;
  error?: string;
  // Legacy fields for backward compatibility
  totalConversations?: number;
  enhancedConversations?: number;
  errors?: number;
  processingTime?: number;
}

export interface UseMigrationReturn {
  isRunning: boolean;
  error: string | null;
  result: MigrationResult | null;
  runConversationEnhancement: (batchSize?: number, forceReprocess?: boolean) => Promise<MigrationResult>;
  clearResult: () => void;
}

/**
 * Hook for running conversation enhancement migration
 */
export function useMigration(): UseMigrationReturn {
  const { currentUser } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MigrationResult | null>(null);

  const runConversationEnhancement = async (
    batchSize = 20,
    forceReprocess = false
  ): Promise<MigrationResult> => {
    if (!currentUser) {
      throw new Error('User must be authenticated to run migration');
    }

    setIsRunning(true);
    setError(null);
    setResult(null);

    try {
      console.log('🚀 Starting conversation enhancement migration...');
      
      // Get the migration function
      const enhanceConversations = httpsCallable(functions, 'enhanceConversations');
      
      // Run migration with parameters
      const migrationParams = {
        userId: currentUser.uid,
        batchSize,
        forceReprocess
      };
      
      console.log('📊 Running migration with parameters:', migrationParams);
      
      const response = await enhanceConversations(migrationParams);
      const migrationResult = response.data as MigrationResult;
      
      console.log('✅ Migration completed successfully!', migrationResult);
      console.log('🔍 Migration details:', {
        totalFound: migrationResult.progress?.totalConversations || migrationResult.totalConversations,
        processed: migrationResult.progress?.processedConversations,
        enhanced: migrationResult.progress?.enhancedConversations || migrationResult.enhancedConversations,
        errors: migrationResult.progress?.errors || migrationResult.errors,
        message: migrationResult.message,
        userId: currentUser.uid
      });
      
      setResult(migrationResult);
      return migrationResult;
      
    } catch (err: any) {
      console.error('❌ Migration failed:', err);
      
      const errorMessage = err?.message || err?.code || 'Migration failed';
      setError(errorMessage);
      
      const failedResult: MigrationResult = {
        success: false,
        totalConversations: 0,
        enhancedConversations: 0,
        errors: 1,
        processingTime: 0,
        error: errorMessage
      };
      
      setResult(failedResult);
      throw err;
      
    } finally {
      setIsRunning(false);
    }
  };

  const clearResult = () => {
    setResult(null);
    setError(null);
  };

  return {
    isRunning,
    error,
    result,
    runConversationEnhancement,
    clearResult
  };
}